import { computed, reactive, ref, watch, type Ref } from 'vue'
import { useEventBus } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import usePut from '@/core/composables/api/usePut'
import { EventKey } from '@/core/events/event-key'
import { ApiRequestStatus } from '@/core/models/api/api-request-status'
import {
  SELF_PERSPECTIVE_KEY,
  type GatheringMechanism,
  type Perspective,
  type PerspectiveGatheringState,
} from '@/core/models/perspective'
import { useSnackStore } from '@/stores/snack.store'
import { useUserStore } from '@/stores/user.store'
import type { PublicSurveyAccess, Survey, SurveyFeedbackTopic } from '../models/survey'

/** One ask, in the shape `PUT survey/:surveyId/gathering` expects. `enabled` is not part of it. */
type GatheringAsk = {
  perspectiveKey: string
  mechanism: GatheringMechanism
  memberIds?: string[]
}

/**
 * Edits who a running personal survey asks.
 *
 * `PUT survey/:surveyId/gathering` takes the template id — the same id as
 * `GET survey/:surveyId` — and replaces the latest round's gathering with the
 * body. Anything left out is removed. Asks that already have feedback are
 * always sent again; the editor does not offer to drop, move, or retarget them.
 * Success is an empty response, so the caller reloads the survey.
 */
export const useEditSurveyPerspectives = (
  survey: Ref<Survey>,
  isOpen: Ref<boolean>,
  onSaved: () => void,
) => {
  const bus = useEventBus(EventKey.surveyUpdated)
  const snackStore = useSnackStore()
  const { t } = useI18n()
  const userStore = useUserStore()

  const endpoint = computed(() => `survey/${survey.value.templateId}/gathering`)
  const { mutate, status: savingStatus } = usePut<void, { gathering: GatheringAsk[] }>(endpoint)

  const form = reactive<{
    includeSelf: boolean
    perspectiveGathering: Record<string, PerspectiveGatheringState>
  }>({
    includeSelf: false,
    perspectiveGathering: {},
  })

  const personalLinkMemberIds = ref<Record<string, string[]>>({})
  const errors = ref<Map<string, string>>(new Map())

  const tandemsFor = (key: string) =>
    survey.value.surveyTandems.filter((tandem) => tandem.perspectiveKey === key)

  const linksFor = (key: string) =>
    survey.value.publicSurveyAccesses.filter((access) => access.perspectiveKey === key)

  /** In-app asks lock once a question has been answered, not when the task is merely open. */
  const lockedMemberIds = (key: string) =>
    tandemsFor(key)
      .filter((tandem) => tandem.sender && tandem.numberOfFeedbacks > 0)
      .map((tandem) => tandem.sender!.id)

  const lockedPersonalLinkIds = (key: string) =>
    linksFor(key)
      .filter((access): access is PublicSurveyAccess & { memberId: string } =>
        Boolean(access.memberId && access.hasFeedback),
      )
      .map((access) => access.memberId)

  const openLinkLocked = (key: string) =>
    linksFor(key).some((access) => !access.memberId && access.hasFeedback)

  const perspectiveLocked = (key: string) =>
    lockedMemberIds(key).length > 0 ||
    lockedPersonalLinkIds(key).length > 0 ||
    openLinkLocked(key)

  const selfTandem = computed(() =>
    survey.value.surveyTandems.find((tandem) => tandem.perspectiveKey === SELF_PERSPECTIVE_KEY),
  )

  const selfLocked = computed(() => (selfTandem.value?.numberOfFeedbacks ?? 0) > 0)

  const includeSelfAvailable = computed(() => {
    const topics = survey.value.feedbackTopics
    return topics.length > 0 && topics.every((topic) => topic.includeSelf)
  })

  const showSelf = computed(() => includeSelfAvailable.value || selfTandem.value != undefined)

  const selfRequired = computed(() =>
    survey.value.feedbackTopics.some(
      (topic) => topic.includeSelf && topic.perspectives.length === 0,
    ),
  )

  const requiredPerspectiveKeys = computed(() => {
    const keys = new Set<string>()
    for (const topic of survey.value.feedbackTopics) {
      if (!topic.includeSelf && topic.perspectives.length === 1) {
        keys.add(topic.perspectives[0].key)
      }
    }
    return keys
  })

  const mechanismsInUse = (key: string): GatheringMechanism[] => {
    const mechanisms = new Set<GatheringMechanism>()
    if (tandemsFor(key).some((tandem) => tandem.sender)) {
      mechanisms.add('app_user')
    }
    if (survey.value.publicSurveyAccesses.some((access) => access.perspectiveKey === key)) {
      mechanisms.add('link')
    }
    return [...mechanisms]
  }

  const availablePerspectives = computed((): Perspective[] => {
    const byKey = new Map<string, Perspective>()

    for (const perspective of intersectPerspectives(survey.value.feedbackTopics)) {
      byKey.set(perspective.key, {
        ...perspective,
        allowedMechanisms: [
          ...new Set<GatheringMechanism>([
            ...perspective.allowedMechanisms,
            ...mechanismsInUse(perspective.key),
          ]),
        ],
      })
    }

    const presentKeys = new Set<string>([
      ...survey.value.surveyTandems.map((tandem) => tandem.perspectiveKey),
      ...survey.value.publicSurveyAccesses.map((access) => access.perspectiveKey),
    ])

    for (const key of presentKeys) {
      if (!key || key === SELF_PERSPECTIVE_KEY || byKey.has(key)) continue
      const fromTopic = survey.value.feedbackTopics
        .flatMap((topic) => topic.perspectives)
        .find((perspective) => perspective.key === key)
      const mechanisms = mechanismsInUse(key)
      if (fromTopic) {
        byKey.set(key, {
          ...fromTopic,
          allowedMechanisms: [
            ...new Set<GatheringMechanism>([...fromTopic.allowedMechanisms, ...mechanisms]),
          ],
        })
      } else {
        byKey.set(key, {
          id: key,
          key,
          title: key,
          nameLong: key,
          nameShort: key,
          icon: 'groups',
          allowedMechanisms: mechanisms.length > 0 ? mechanisms : ['app_user'],
        })
      }
    }

    return [...byKey.values()]
  })

  const isLinkOnly = (perspective: Perspective) =>
    perspective.allowedMechanisms.length === 1 && perspective.allowedMechanisms[0] === 'link'

  const ensurePerspectiveState = (key: string) => {
    if (!form.perspectiveGathering[key]) {
      form.perspectiveGathering[key] = {
        enabled: false,
        appUserMemberIds: [],
        useOpenLink: false,
      }
    }
    return form.perspectiveGathering[key]
  }

  const resetFromSurvey = () => {
    errors.value.clear()
    const next: Record<string, PerspectiveGatheringState> = {}
    const links: Record<string, string[]> = {}

    for (const perspective of availablePerspectives.value) {
      next[perspective.key] = {
        enabled: requiredPerspectiveKeys.value.has(perspective.key),
        appUserMemberIds: [],
        useOpenLink: isLinkOnly(perspective),
      }
    }

    form.includeSelf = false

    for (const tandem of survey.value.surveyTandems) {
      if (tandem.perspectiveKey === SELF_PERSPECTIVE_KEY) {
        form.includeSelf = true
        continue
      }
      if (!tandem.sender) continue
      const state = next[tandem.perspectiveKey]
      if (!state) continue
      state.enabled = true
      if (!state.appUserMemberIds.includes(tandem.sender.id)) {
        state.appUserMemberIds.push(tandem.sender.id)
      }
    }

    for (const access of survey.value.publicSurveyAccesses) {
      if (!access.perspectiveKey) continue
      const state = next[access.perspectiveKey]
      if (!state) continue
      state.enabled = true
      if (access.memberId) {
        links[access.perspectiveKey] = [...(links[access.perspectiveKey] ?? []), access.memberId]
      } else {
        state.useOpenLink = true
      }
    }

    for (const perspective of availablePerspectives.value) {
      if (!perspectiveLocked(perspective.key)) continue
      next[perspective.key].enabled = true
      if (openLinkLocked(perspective.key)) {
        next[perspective.key].useOpenLink = true
      }
    }

    if (selfRequired.value || selfLocked.value) {
      form.includeSelf = true
    }

    form.perspectiveGathering = next
    personalLinkMemberIds.value = links
  }

  watch(isOpen, (open) => {
    if (open) resetFromSurvey()
  })

  watch([selfRequired, selfLocked], () => {
    if (selfRequired.value || selfLocked.value) {
      form.includeSelf = true
    }
  })

  const selectedMemberIds = computed(() => {
    const fromPerspectives = availablePerspectives.value.flatMap((perspective) => {
      const state = form.perspectiveGathering[perspective.key]
      if (!state?.enabled) return []
      return [
        ...state.appUserMemberIds,
        ...(personalLinkMemberIds.value[perspective.key] ?? []),
      ]
    })
    return [...new Set(fromPerspectives)]
  })

  const buildGathering = (): GatheringAsk[] => {
    const gathering: GatheringAsk[] = []

    if (form.includeSelf || selfLocked.value) {
      gathering.push({
        perspectiveKey: SELF_PERSPECTIVE_KEY,
        mechanism: 'app_user',
        memberIds: [userStore.user.id],
      })
    }

    for (const perspective of availablePerspectives.value) {
      const state = form.perspectiveGathering[perspective.key]
      if (!state) continue

      const answeredIds = lockedMemberIds(perspective.key)
      const lockedLinks = lockedPersonalLinkIds(perspective.key)
      const keepOpenLink = openLinkLocked(perspective.key)
      if (!state.enabled && !perspectiveLocked(perspective.key)) continue

      if (perspective.allowedMechanisms.includes('app_user')) {
        const memberIds = [...new Set([...state.appUserMemberIds, ...answeredIds])]
        if (memberIds.length > 0) {
          gathering.push({
            perspectiveKey: perspective.key,
            mechanism: 'app_user',
            memberIds,
          })
        }
      }

      if (
        perspective.allowedMechanisms.includes('link') &&
        (state.useOpenLink || isLinkOnly(perspective) || keepOpenLink)
      ) {
        gathering.push({
          perspectiveKey: perspective.key,
          mechanism: 'link',
        })
      }

      const personalIds = [
        ...new Set([...(personalLinkMemberIds.value[perspective.key] ?? []), ...lockedLinks]),
      ]
      if (personalIds.length > 0 && perspective.allowedMechanisms.includes('link')) {
        gathering.push({
          perspectiveKey: perspective.key,
          mechanism: 'link',
          memberIds: personalIds,
        })
      }
    }

    return gathering
  }

  const validate = () => {
    errors.value.clear()

    if (buildGathering().length === 0) {
      errors.value.set('perspectives', t('startSurvey.gatheringRequired'))
      return
    }

    const memberIdToPerspectiveKeys = new Map<string, string[]>()

    for (const perspective of availablePerspectives.value) {
      const state = form.perspectiveGathering[perspective.key]
      if (!state?.enabled) continue

      const personalIds = personalLinkMemberIds.value[perspective.key] ?? []
      const hasAppUser =
        perspective.allowedMechanisms.includes('app_user') && state.appUserMemberIds.length > 0
      const hasOpenLink =
        perspective.allowedMechanisms.includes('link') &&
        (state.useOpenLink || isLinkOnly(perspective))
      const hasPersonalLink =
        personalIds.length > 0 && perspective.allowedMechanisms.includes('link')

      if (!hasAppUser && !hasOpenLink && !hasPersonalLink) {
        errors.value.set(perspective.key, t('feedback.formValidation.members'))
      }

      if (perspective.allowedMechanisms.includes('app_user')) {
        for (const memberId of new Set([...state.appUserMemberIds, ...personalIds])) {
          const keys = memberIdToPerspectiveKeys.get(memberId) ?? []
          keys.push(perspective.key)
          memberIdToPerspectiveKeys.set(memberId, keys)
        }
      } else {
        for (const memberId of personalIds) {
          const keys = memberIdToPerspectiveKeys.get(memberId) ?? []
          keys.push(perspective.key)
          memberIdToPerspectiveKeys.set(memberId, keys)
        }
      }
    }

    for (const keys of memberIdToPerspectiveKeys.values()) {
      if (keys.length > 1) {
        for (const key of keys) {
          errors.value.set(key, t('feedback.formValidation.duplicateMembers'))
        }
      }
    }

    for (const topic of survey.value.feedbackTopics) {
      const hasSelf = form.includeSelf && (topic.includeSelf || selfLocked.value)
      const hasPerspective = topic.perspectives.some(
        (perspective) => form.perspectiveGathering[perspective.key]?.enabled,
      )
      if (!hasSelf && !hasPerspective) {
        errors.value.set(
          'perspectives',
          t('startSurvey.perspectivesRequired', { templateTitle: topic.title }),
        )
      }
    }
  }

  watch(form, () => {
    errors.value.clear()
  })

  const save = async () => {
    validate()
    if (errors.value.size > 0) return

    await mutate({ gathering: buildGathering() })

    if (savingStatus.value === ApiRequestStatus.Success) {
      bus.emit()
      snackStore.showInfo(t('survey.editPerspectivesSuccess'))
      onSaved()
      return
    }

    if (savingStatus.value === ApiRequestStatus.Error) {
      snackStore.showError(t('survey.editPerspectivesError'))
    }
  }

  const isSaving = computed(() => savingStatus.value === ApiRequestStatus.Loading)

  return {
    form,
    errors,
    availablePerspectives,
    showSelf,
    selfRequired,
    selfLocked,
    requiredPerspectiveKeys,
    ensurePerspectiveState,
    selectedMemberIds,
    lockedMemberIds,
    openLinkLocked,
    perspectiveLocked,
    save,
    isSaving,
  }
}

const intersectPerspectives = (topics: ReadonlyArray<SurveyFeedbackTopic>): Perspective[] => {
  if (topics.length === 0) return []

  const [first, ...rest] = topics
  let intersected: Perspective[] = first.perspectives.map((perspective) => ({
    ...perspective,
    allowedMechanisms: [...perspective.allowedMechanisms],
  }))

  for (const topic of rest) {
    intersected = intersected
      .filter((perspective) =>
        topic.perspectives.some((topicPerspective) => topicPerspective.key === perspective.key),
      )
      .map((perspective) => {
        const other = topic.perspectives.find(
          (topicPerspective) => topicPerspective.key === perspective.key,
        )!
        return {
          ...perspective,
          allowedMechanisms: perspective.allowedMechanisms.filter((mechanism) =>
            other.allowedMechanisms.includes(mechanism),
          ),
        }
      })
      .filter((perspective) => perspective.allowedMechanisms.length > 0)
  }

  return intersected
}
